import * as React from 'react';
import { cn } from '@/lib/utils';

/** Maximum number of toasts to display at once */
const MAX_TOASTS = 5;

/** Default toast duration in milliseconds */
const DEFAULT_DURATION = 3000;

/** Minimum toast duration in milliseconds */
const MIN_DURATION = 1000;

/** Maximum toast duration in milliseconds */
const MAX_DURATION = 30000;

/**
 * Generates a cryptographically secure unique ID
 * Falls back to a timestamp-based ID if crypto is unavailable
 */
function generateUniqueId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Last resort fallback (less secure but functional)
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export interface Toast {
  id: string;
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type?: Toast['type'], duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timeoutRefs = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    const currentTimeouts = timeoutRefs.current;
    return () => {
      currentTimeouts.forEach((timeout) => clearTimeout(timeout));
      currentTimeouts.clear();
    };
  }, []);

  const removeToast = React.useCallback((id: string) => {
    // Clear the timeout for this toast
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = React.useCallback(
    (message: string, type: Toast['type'] = 'info', duration: number = DEFAULT_DURATION) => {
      // Validate message
      if (typeof message !== 'string' || !message.trim()) {
        return;
      }

      // Validate and clamp duration
      let validDuration = DEFAULT_DURATION;
      if (typeof duration === 'number' && Number.isFinite(duration)) {
        validDuration = Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.floor(duration)));
      }

      // Validate type
      const validTypes = ['success', 'error', 'info'] as const;
      const validType = validTypes.includes(type as typeof validTypes[number]) ? type : 'info';

      const id = generateUniqueId();
      const newToast: Toast = {
        id,
        message: message.trim(),
        type: validType,
        duration: validDuration,
      };

      setToasts((prev) => {
        // Remove oldest toasts if we exceed the limit
        const updated = [...prev, newToast];
        if (updated.length > MAX_TOASTS) {
          const toRemove = updated.slice(0, updated.length - MAX_TOASTS);
          toRemove.forEach((toast) => {
            const timeout = timeoutRefs.current.get(toast.id);
            if (timeout) {
              clearTimeout(timeout);
              timeoutRefs.current.delete(toast.id);
            }
          });
          return updated.slice(-MAX_TOASTS);
        }
        return updated;
      });

      // Auto-remove after duration
      const timeoutId = setTimeout(() => {
        removeToast(id);
      }, validDuration);
      timeoutRefs.current.set(id, timeoutId);
    },
    [removeToast]
  );

  const contextValue = React.useMemo(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const typeStyles: Readonly<Record<NonNullable<Toast['type']>, string>> = Object.freeze({
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-slate-800 text-white',
});

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const handleRemove = React.useCallback(() => {
    onRemove(toast.id);
  }, [onRemove, toast.id]);

  return (
    <div
      className={cn(
        'px-4 py-3 rounded-lg shadow-lg pointer-events-auto animate-in slide-in-from-bottom-5 fade-in duration-300',
        typeStyles[toast.type || 'info']
      )}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">{toast.message}</span>
        <button
          onClick={handleRemove}
          className="ml-2 hover:opacity-80 transition-opacity"
          aria-label="Close notification"
          type="button"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
